<template>
  <div v-if="isOpen" class="ai-assistant-widget" :class="{ 'minimized': isMinimized, 'dark-mode': isDarkMode }">
    <!-- Widget Header -->
    <div class="ai-assistant-header" @mousedown="startDrag">
      <div class="header-left">
        <div class="ai-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </div>
        <div class="header-info">
          <h3>AI Assistant</h3>
          <span v-if="isStruggling" class="struggle-indicator">💡 I'm here to help</span>
          <span v-else-if="isLoading" class="status-indicator">Thinking...</span>
          <span v-else class="status-indicator">Ready to assist</span>
        </div>
      </div>
      <div class="header-actions">
        <button @click="minimize" class="btn-icon" title="Minimize">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 8h8v2H4z"/>
          </svg>
        </button>
        <button @click="close" class="btn-icon" title="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Widget Body -->
    <div v-if="!isMinimized" class="ai-assistant-body">
      <!-- Welcome Message -->
      <div v-if="!hasConversation" class="welcome-section">
        <div class="welcome-icon">👋</div>
        <h4>Hi! I'm your AI Assistant</h4>
        <p>I can help you with:</p>
        <ul class="help-list">
          <li>Navigating the CRM</li>
          <li>Resolving errors</li>
          <li>Finding features</li>
          <li>Completing tasks</li>
        </ul>
        <p class="help-tip">Just type your question below!</p>
      </div>

      <!-- Struggle Alert -->
      <div v-if="isStruggling && suggestions.length > 0" class="struggle-alert">
        <div class="alert-icon">💡</div>
        <div class="alert-content">
          <p><strong>Need help?</strong> I noticed you might be having trouble.</p>
          <div class="suggestions">
            <button
              v-for="(suggestion, index) in suggestions"
              :key="index"
              @click="applySuggestion(suggestion)"
              class="suggestion-btn"
            >
              {{ suggestion.message }}
            </button>
          </div>
        </div>
      </div>

      <!-- Chat Messages -->
      <div ref="messagesContainer" class="messages-container">
        <ChatMessage
          v-for="message in conversationHistory"
          :key="message.id"
          :message="message"
          @mark-helpful="markHelpful"
          @mark-not-helpful="markNotHelpful"
          @send-followup="sendFollowUpQuestion"
          @start-guide="startGuide"
        />
        
        <!-- Typing Indicator -->
        <div v-if="isLoading" class="typing-indicator">
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
          <div class="typing-dot"></div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input-container">
        <ChatInput
          v-model="currentMessage"
          :disabled="isLoading"
          :placeholder="inputPlaceholder"
          @send="sendMessage"
          @cancel="clearInput"
        />
      </div>
    </div>

    <!-- Minimized State -->
    <div v-else class="minimized-content" @click="maximize">
      <span class="minimized-text">AI Assistant</span>
      <span v-if="isStruggling" class="notification-badge">!</span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ChatMessage from './ChatMessage.vue';
import ChatInput from './ChatInput.vue';

export default {
  name: 'AIAssistantWidget',

  components: {
    ChatMessage,
    ChatInput,
  },

  data() {
    return {
      currentMessage: '',
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
    };
  },

  computed: {
    ...mapGetters('aiAssistant', [
      'isOpen',
      'isMinimized',
      'conversationHistory',
      'isLoading',
      'isStruggling',
      'suggestions',
      'hasConversation',
    ]),

    isDarkMode() {
      return this.$store.state.darkMode;
    },

    inputPlaceholder() {
      if (this.isLoading) return 'AI is thinking...';
      if (this.isStruggling) return 'Tell me what\'s wrong...';
      return 'Ask me anything...';
    },
  },

  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.updateContext();
        this.checkStruggle();
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    conversationHistory() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
  },

  mounted() {
    // Initialize context
    this.updateContext();

    // Check struggle status periodically
    this.struggleCheckInterval = setInterval(() => {
      if (this.isOpen) {
        this.checkStruggle();
      }
    }, 30000); // Check every 30 seconds

    // Keyboard shortcut: Ctrl+K or Cmd+K
    document.addEventListener('keydown', this.handleKeyboardShortcut);

    // Handle mouse move for dragging
    document.addEventListener('mousemove', this.handleDrag);
    document.addEventListener('mouseup', this.stopDrag);
  },

  beforeDestroy() {
    if (this.struggleCheckInterval) {
      clearInterval(this.struggleCheckInterval);
    }
    document.removeEventListener('keydown', this.handleKeyboardShortcut);
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },

  methods: {
    ...mapActions('aiAssistant', [
      'open',
      'close',
      'minimize',
      'maximize',
      'sendMessage',
      'updateContext',
      'checkStruggle',
      'markAsHelpful',
      'markAsNotHelpful',
    ]),

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isLoading) return;

      const message = this.currentMessage.trim();
      this.currentMessage = '';

      try {
        await this.$store.dispatch('aiAssistant/sendMessage', {
          message,
          context: {
            page: window.location.pathname,
            action: 'chat_message',
          },
        });
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    },

    clearInput() {
      this.currentMessage = '';
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    markHelpful(messageId) {
      const message = this.conversationHistory.find(m => m.id === messageId);
      this.$store.dispatch('aiAssistant/markAsHelpful', {
        messageId,
        conversationUuid: message?.conversationUuid,
      });
    },

    markNotHelpful(messageId) {
      const message = this.conversationHistory.find(m => m.id === messageId);
      this.$store.dispatch('aiAssistant/markAsNotHelpful', {
        messageId,
        conversationUuid: message?.conversationUuid,
        feedback: 'Not helpful', // Could prompt for more detailed feedback
      });
    },

    sendFollowUpQuestion(question) {
      this.currentMessage = question;
      this.sendMessage();
    },

    startGuide(action) {
      // Could implement interactive guides in the future
      console.log('Start guide:', action);
    },

    applySuggestion(suggestion) {
      if (suggestion.action === 'open_ai_assistant') {
        // Already open, just focus input
        this.$nextTick(() => {
          const input = this.$el.querySelector('input, textarea');
          if (input) input.focus();
        });
      } else if (suggestion.action === 'show_tutorial') {
        this.currentMessage = 'Can you show me a tutorial for this page?';
        this.sendMessage();
      } else if (suggestion.action === 'open_search') {
        this.currentMessage = 'Help me find what I\'m looking for';
        this.sendMessage();
      } else if (suggestion.action === 'show_form_help') {
        this.currentMessage = 'I need help with this form';
        this.sendMessage();
      }
    },

    handleKeyboardShortcut(e) {
      // Ctrl+K or Cmd+K to toggle
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.$store.dispatch('aiAssistant/toggle');
      }

      // Escape to close
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    },

    startDrag(e) {
      this.isDragging = true;
      const rect = this.$el.getBoundingClientRect();
      this.dragOffset = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },

    handleDrag(e) {
      if (!this.isDragging) return;

      const x = e.clientX - this.dragOffset.x;
      const y = e.clientY - this.dragOffset.y;

      // Keep widget within viewport
      const maxX = window.innerWidth - this.$el.offsetWidth;
      const maxY = window.innerHeight - this.$el.offsetHeight;

      this.$el.style.left = `${Math.max(0, Math.min(x, maxX))}px`;
      this.$el.style.top = `${Math.max(0, Math.min(y, maxY))}px`;
      this.$el.style.right = 'auto';
      this.$el.style.bottom = 'auto';
    },

    stopDrag() {
      this.isDragging = false;
    },
  },
};
</script>

<style scoped lang="scss">
.ai-assistant-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 400px;
  max-width: calc(100vw - 40px);
  height: 600px;
  max-height: calc(100vh - 40px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  z-index: 9999;
  transition: all 0.3s ease;

  &.minimized {
    height: 60px;
    cursor: pointer;
  }

  &.dark-mode {
    background: #1e1e1e;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }
}

.ai-assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border-radius: 16px 16px 0 0;
  cursor: move;
  user-select: none;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .ai-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 2px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .status-indicator,
    .struggle-indicator {
      font-size: 12px;
      opacity: 0.9;
    }

    .struggle-indicator {
      animation: pulse 2s infinite;
    }
  }

  .header-actions {
    display: flex;
    gap: 8px;

    .btn-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: rgba(255, 255, 255, 0.2);
      border: none;
      border-radius: 8px;
      color: #ffffff;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.3);
      }
    }
  }
}

.ai-assistant-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.welcome-section {
  padding: 24px;
  text-align: center;

  .welcome-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  h4 {
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
  }

  p {
    margin: 8px 0;
    color: #666;
    font-size: 14px;
  }

  .help-list {
    list-style: none;
    padding: 0;
    margin: 16px 0;
    text-align: left;

    li {
      padding: 8px 0;
      color: #666;
      font-size: 14px;

      &:before {
        content: '✓ ';
        color: #667eea;
        font-weight: bold;
        margin-right: 8px;
      }
    }
  }

  .help-tip {
    margin-top: 16px;
    padding: 12px;
    background: #f0f4ff;
    border-radius: 8px;
    color: #667eea;
    font-size: 13px;
    font-weight: 500;
  }

  .dark-mode & {
    h4 {
      color: #e0e0e0;
    }

    p,
    .help-list li {
      color: #b0b0b0;
    }

    .help-tip {
      background: #2a2a2a;
      color: #8b9aed;
    }
  }
}

.struggle-alert {
  margin: 16px;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 12px;
  display: flex;
  gap: 12px;
  animation: slideIn 0.3s ease;

  .alert-icon {
    font-size: 24px;
  }

  .alert-content {
    flex: 1;

    p {
      margin: 0 0 12px 0;
      color: #856404;
      font-size: 14px;
    }

    .suggestions {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .suggestion-btn {
      padding: 8px 12px;
      background: #ffffff;
      border: 1px solid #ffc107;
      border-radius: 8px;
      color: #856404;
      font-size: 13px;
      cursor: pointer;
      text-align: left;
      transition: all 0.2s;

      &:hover {
        background: #ffc107;
        color: #ffffff;
      }
    }
  }

  .dark-mode & {
    background: #3a3a00;
    border-color: #ffc107;

    .alert-content p {
      color: #ffc107;
    }

    .suggestion-btn {
      background: #2a2a2a;
      color: #ffc107;

      &:hover {
        background: #ffc107;
        color: #000000;
      }
    }
  }
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;

    &:hover {
      background: #b0b0b0;
    }
  }

  .dark-mode & {
    &::-webkit-scrollbar-thumb {
      background: #4a4a4a;

      &:hover {
        background: #5a5a5a;
      }
    }
  }
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 16px;
  width: fit-content;

  .typing-dot {
    width: 8px;
    height: 8px;
    background: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  .dark-mode & {
    background: #2a2a2a;

    .typing-dot {
      background: #666;
    }
  }
}

.chat-input-container {
  padding: 16px;
  border-top: 1px solid #e0e0e0;

  .dark-mode & {
    border-top-color: #3a3a3a;
  }
}

.minimized-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 100%;
  cursor: pointer;

  .minimized-text {
    color: #ffffff;
    font-weight: 600;
  }

  .notification-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: #ff4444;
    color: #ffffff;
    border-radius: 50%;
    font-size: 14px;
    font-weight: bold;
    animation: pulse 2s infinite;
  }
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive
@media (max-width: 768px) {
  .ai-assistant-widget {
    width: calc(100vw - 32px);
    height: calc(100vh - 32px);
    bottom: 16px;
    right: 16px;
    border-radius: 12px;

    .ai-assistant-header {
      border-radius: 12px 12px 0 0;
    }
  }
}
</style>
